/**
 * @file microbiological_data_explorer_screen.js
 * @description Screen for exploring microbiological data with hierarchical tree structure
 *
 * Features:
 * - Displays NOMOS_ESTRAZIONIMICRO data with filters
 * - Click a row to open a right-side drawer with related LIS details
 * - Error highlighting
 * - Pagination
 */

import React, { useEffect, useContext, useState, useMemo, useRef } from "react";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { MicrobiologicalDataLabels } from "../_label/lis_labels/lis_labels.js";
import Select from "react-select";
import LisTableHeader from "../../components/lis/LisTableHeader.js";
import LisMainTable from "../../components/lis/LisMainTable.js";

const MicrobiologicalDataExplorerScreen = () => {
  const { currentUser } = useContext(UserContext);
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  // State management
  const [data, setData] = useState([]);
  const [searchList, setSearchList] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lisDetails, setLisDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showReset, setShowReset] = useState(false);



  // Collapsed sections state - tracks which sections are collapsed
  const [collapsedSections, setCollapsedSections] = useState({
    errorLegend: true,
    sourceData: false,
    lisHeader: false,
    lisMaterials: false,
  });

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Per-culture and per-microorganism collapse state (keyed by ID)
  const [collapsedCultures, setCollapsedCultures] = useState({});
  const [collapsedMicroorganisms, setCollapsedMicroorganisms] = useState({});

  const toggleCulture = (id) =>
    setCollapsedCultures((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleMicroorganism = (id) =>
    setCollapsedMicroorganisms((prev) => ({ ...prev, [id]: !prev[id] }));

  const [collapsedMicroSections, setCollapsedMicroSections] = useState({});
  const toggleMicroSection = (cultureId) =>
    setCollapsedMicroSections((prev) => ({ ...prev, [cultureId]: !prev[cultureId] }));

  // Filter state
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    businessUnit: "",
    testDescription: "",
    microbeName: "",
    antibioticName: "",
    gender: "",
    errorFilter: null,
    requestId: "",
  });

  // Allow both page-level scroll and inner table scroll
  const CONTENT_SCROLL_HEIGHT = "calc(100vh - 140px)";
  const TABLE_SCROLL_HEIGHT = "calc(100vh - 360px)";
  const DRAWER_WIDTH = 700;

  const toolbarItemWidthPx = 200;
  const compactHeightPx = 42;
  const uniformControlStyle = {
    width: toolbarItemWidthPx,
    minWidth: toolbarItemWidthPx,
    height: compactHeightPx,
    minHeight: compactHeightPx,
  };
  const noFilters =
    filters.fromDate === "" &&
    filters.toDate === "" &&
    filters.businessUnit === "" &&
    filters.testDescription === "" &&
    filters.microbeName === "" &&
    filters.antibioticName === "" &&
    filters.gender === "" &&
    filters.errorFilter === null &&
    filters.requestId === "";

  // Dynamically size the table container similar to DMS TableComponent
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(550);
  useEffect(() => {
    const recalc = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight =
        (window.visualViewport && window.visualViewport.height) ||
        window.innerHeight;
      const bottomMargin = viewportHeight < 700 ? 48 : 24;
      const available = viewportHeight - rect.top - bottomMargin;
      const maxCap = 550;
      const height = Math.max(0, Math.min(available, maxCap));
      setContainerHeight(height);
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("orientationchange", recalc);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", recalc);
    }
    const tId = setTimeout(recalc, 0);
    return () => {
      clearTimeout(tId);
      window.removeEventListener("resize", recalc);
      window.removeEventListener("orientationchange", recalc);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", recalc);
      }
    };
  }, []);
  // Initialize i18n labels
  const i18n = menu.getI18n();
  i18n.addResourceBundle(
    "it",
    "microDataLabels",
    MicrobiologicalDataLabels.it,
    true,
    true
  );
  i18n.addResourceBundle(
    "en",
    "microDataLabels",
    MicrobiologicalDataLabels.en,
    true,
    true
  );

  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      {
        link: "/admin/microbiological_data_explorer",
        name: t("screenTitle", { ns: "microDataLabels" }),
      },
    ];
    breadcrumb.set(path);

    // Write menu
    menu.write({ user: currentUser() });

    // Initial load
    fetchData({ page: 1, pageSize: 50 });
  }, []);

  // Close drawer with ESC
  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsDrawerOpen(false);
        setSelectedRequestId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDrawerOpen]);

  /**
   * Fetch paginated data from API
   */
  const fetchData = async (params = {}) => {
    const { page = 1, pageSize = 50, ...filterParams } = params;

    console.log("[Frontend] Fetching data with params:", {
      page,
      pageSize,
      ...filterParams,
    });

    try {
      setIsLoading(true);
      const response = await api.sendMessage(
        `admin/lis/getMicrobiologicalDataExplorer`,
        {
          user: currentUser(),
          body: {
            page,
            pageSize,
            ...filterParams,
          },
        }
      );

      console.log("[Frontend] API response:", response);

      if (response.success) {
        setData(response.data || []);
        setSearchList(response.data || []);
        setPagination(response.pagination || null);
      } else {
        console.error("[Frontend] API returned success=false:", response);
      }
    } catch (error) {
      console.error("Error fetching microbiological data:", error);
      MySwal.fire({
        icon: "error",
        title: t("errorLoadingData", { ns: "microDataLabels" }),
        text: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Numbered pagination button sizing similar to DMS TableComponent
  const numberButtonStyle = useMemo(() => {
    const total = pagination?.totalPages || 0;
    const digits = String(total).length;
    let fontSizeRem = 1.0;
    let horizontalPaddingRem = 0.75;
    let verticalPaddingRem = 0.375;

    if (digits >= 6) {
      fontSizeRem = 0.75;
      horizontalPaddingRem = 0.4;
      verticalPaddingRem = 0.3;
    } else if (digits >= 5) {
      fontSizeRem = 0.8;
      horizontalPaddingRem = 0.5;
      verticalPaddingRem = 0.3;
    } else if (digits === 4) {
      fontSizeRem = 0.9;
      horizontalPaddingRem = 0.6;
      verticalPaddingRem = 0.325;
    }

    return {
      fontSize: `${fontSizeRem}rem`,
      padding: `${verticalPaddingRem}rem ${horizontalPaddingRem}rem`,
      lineHeight: 1.25,
      whiteSpace: "nowrap",
    };
  }, [pagination?.totalPages]);

  // Compute the set of page numbers to display (max 5)
  const getPagedNumbers = () => {
    const current = pagination?.currentPage || 1;
    const total = pagination?.totalPages || 1;
    const maxButtons = 5;
    let startPage = Math.max(1, current - Math.floor(maxButtons / 2));
    let endPage = Math.min(total, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  /**
   * Fetch detailed LIS data for a specific request
   */
  const fetchLisDetails = async (idRichiesta) => {
    if (lisDetails[idRichiesta]) {
      // Already loaded
      return;
    }

    setLoadingDetails((prev) => new Set([...prev, idRichiesta]));

    try {
      const response = await api.sendMessage(
        `admin/lis/getMicrobiologicalDataDetails`,
        {
          user: currentUser(),
          body: {
            id_richiesta: idRichiesta,
          },
        }
      );

      if (response.success) {
        setLisDetails((prev) => ({
          ...prev,
          [idRichiesta]: response.data,
        }));
      }
    } catch (error) {
      console.error("Error fetching LIS details:", error);
      MySwal.fire({
        icon: "error",
        title: t("errorLoadingDetails", { ns: "microDataLabels" }),
        text: error.message,
      });
    } finally {
      setLoadingDetails((prev) => {
        const newSet = new Set(prev);
        newSet.delete(idRichiesta);
        return newSet;
      });
    }
  };

  /**
   * Open/close the right-side drawer for a request
   */
  const openDrawerForRequest = (idRichiesta) => {
    setSelectedRequestId(idRichiesta);
    setIsDrawerOpen(true);
    fetchLisDetails(idRichiesta);

    // Reset drawer scroll position when changing record
    setTimeout(() => {
      const el = document.querySelector(".micro-data-drawer-content");
      if (el) el.scrollTop = 0;
    }, 0);
  };

  const toggleDrawerForRequest = (idRichiesta) => {
    // Clicking the same selected row toggles close/open
    if (isDrawerOpen && selectedRequestId === idRichiesta) {
      setIsDrawerOpen(false);
      setSelectedRequestId(null);
      return;
    }

    openDrawerForRequest(idRichiesta);
  };

  /**
   * Handle search with filters
   */
  const handleSearch = () => {
    fetchData({
      page: 1,
      pageSize: pagination?.pageSize ?? 50,
      ...filters,
    });
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      businessUnit: "",
      testDescription: "",
      microbeName: "",
      antibioticName: "",
      gender: "",
      errorFilter: null,
      requestId: "",
    });
    setSearchList(null);
    setShowReset(false);
    fetchData({ page: 1, pageSize: pagination?.pageSize ?? 50 });
  };

  /**
   * Handle pagination
   */
  const handlePageChange = (newPage) => {
    fetchData({
      page: newPage,
      pageSize: pagination?.pageSize ?? 50,
      ...filters,
    });
  };

  /**
   * Format date for display
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  /**
   * Check if record has any errors
   */
  const hasErrors = (record) => {
    return (
      record.errors && Object.values(record.errors).some((val) => val === 1)
    );
  };

  /**
   * Map NOMOS error flags to i18n label keys.
   */
  const ERROR_FLAG_TO_LABEL_KEY = {
    id_reparto_error: "departmentError",
    id_analisi_error: "analysisError",
    id_materiale_error: "materialError",
    id_provenienza_error: "provenanceError",
    id_micro_error: "microError",
    id_antibiotico_error: "antibioticError",
  };

  const ERROR_FLAG_TO_TOOLTIP_KEY = {
    id_reparto_error: "departmentErrorTooltip",
    id_analisi_error: "analysisErrorTooltip",
    id_materiale_error: "materialErrorTooltip",
    id_provenienza_error: "provenanceErrorTooltip",
    id_micro_error: "microErrorTooltip",
    id_antibiotico_error: "antibioticErrorTooltip",
  };

  const getErrorLabelKeys = (errors) => {
    if (!errors) return [];
    return Object.entries(ERROR_FLAG_TO_LABEL_KEY)
      .filter(([flagKey]) => errors?.[flagKey] === 1)
      .map(([, labelKey]) => labelKey);
  };

  const renderErrorChip = (labelKey, flagKey) => (
    <span
      key={labelKey}
      className="badge badge-danger badge-sm ms-2"
      title={t(ERROR_FLAG_TO_TOOLTIP_KEY[flagKey], { ns: "microDataLabels" })}
      style={{ cursor: "help" }}
    >
      <i className="fas fa-exclamation-triangle me-1"></i>
      {t(labelKey, { ns: "microDataLabels" })}
    </span>
  );

  /**
   * Render error legend
   */
  const renderErrorLegend = () => {
    const isCollapsed = collapsedSections.errorLegend;

    return (
      <div className="alert alert-warning mb-3">
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => toggleSection("errorLegend")}
        >
          <h6 className="alert-heading mb-0">
            <i className="fas fa-info-circle me-2"></i>
            {t("errorLegendTitle", { ns: "microDataLabels" })}
          </h6>
          <i className={`fas fa-chevron-${isCollapsed ? "down" : "up"}`}></i>
        </div>

        {!isCollapsed && (
          <>
            <p className="mb-3 small mt-2">
              {t("errorLegendDescription", { ns: "microDataLabels" })}
            </p>
            <div className="row small">
              <div className="col-md-6">
                <div className="d-flex flex-column align-items-start mb-3">
                  <span className="badge badge-danger badge-sm mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {t("departmentError", { ns: "microDataLabels" })}
                  </span>
                  <span className="text-muted small">
                    {t("departmentErrorTooltip", { ns: "microDataLabels" })}
                  </span>
                </div>
                <div className="d-flex flex-column align-items-start mb-3">
                  <span className="badge badge-danger badge-sm mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {t("materialError", { ns: "microDataLabels" })}
                  </span>
                  <span className="text-muted small">
                    {t("materialErrorTooltip", { ns: "microDataLabels" })}
                  </span>
                </div>
                <div className="d-flex flex-column align-items-start mb-3">
                  <span className="badge badge-danger badge-sm mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {t("provenanceError", { ns: "microDataLabels" })}
                  </span>
                  <span className="text-muted small">
                    {t("provenanceErrorTooltip", { ns: "microDataLabels" })}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex flex-column align-items-start mb-3">
                  <span className="badge badge-danger badge-sm mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {t("microError", { ns: "microDataLabels" })}
                  </span>
                  <span className="text-muted small">
                    {t("microErrorTooltip", { ns: "microDataLabels" })}
                  </span>
                </div>
                <div className="d-flex flex-column align-items-start mb-3">
                  <span className="badge badge-danger badge-sm mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {t("antibioticError", { ns: "microDataLabels" })}
                  </span>
                  <span className="text-muted small">
                    {t("antibioticErrorTooltip", { ns: "microDataLabels" })}
                  </span>
                </div>
                <div className="d-flex flex-column align-items-start mb-3">
                  <span className="badge badge-danger badge-sm mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {t("analysisError", { ns: "microDataLabels" })}
                  </span>
                  <span className="text-muted small">
                    {t("analysisErrorTooltip", { ns: "microDataLabels" })}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  /**
   * Render source data section
   */
  const renderSourceData = (record) => {
    const isCollapsed = collapsedSections.sourceData;

    return (
      <div className="card mb-3">
        <div
          className="card-header bg-light d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => toggleSection("sourceData")}
        >
          <h5 className="mb-0 flex-grow-1">
            <i className="fas fa-database me-2"></i>
            {t("sourceDataTitle", { ns: "microDataLabels" })}
          </h5>
          <i
            className={`fas fa-chevron-${isCollapsed ? "down" : "up"} ms-2`}
            style={{ fontSize: "1.2rem" }}
          ></i>
        </div>
        {!isCollapsed && (
          <div className="card-body py-2 px-3">
            {record.records.map((rec, idx) => (
              <div key={idx} className={idx < record.records.length - 1 ? "pb-2 mb-2 border-bottom" : ""}>
                <dl className="row mb-0 small" style={{ rowGap: "0.15rem" }}>
                  <dt className="col-5 text-muted fw-normal mb-0">{t("sourceId", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0 fw-semibold">{rec.id}</dd>

                  <dt className="col-5 text-muted fw-normal mb-0">{t("materialDescription", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0">{rec.descrizione_materiale || "-"}</dd>

                  <dt className="col-5 text-muted fw-normal mb-0">{t("provenanceDescription", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0">{rec.descrizione_provenienza || "-"}</dd>

                  <dt className="col-5 text-muted fw-normal mb-0">{t("microDescription", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0">{rec.descrizione_micro || "-"}</dd>

                  {rec.descrizione_antibio && <>
                    <dt className="col-5 text-muted fw-normal mb-0">{t("antibioticDescription", { ns: "microDataLabels" })}</dt>
                    <dd className="col-7 mb-0">{rec.descrizione_antibio}</dd>
                  </>}

                  {(rec.rsi || rec.mic) && <>
                    <dt className="col-5 text-muted fw-normal mb-0">{t("rsi", { ns: "microDataLabels" })} / {t("mic", { ns: "microDataLabels" })}</dt>
                    <dd className="col-7 mb-0 d-flex align-items-center gap-2">
                      <span className={`badge ${rec.rsi === "R" ? "badge-danger" : rec.rsi === "S" ? "badge-success" : rec.rsi ? "badge-warning" : "bg-secondary"}`}>
                        {rec.rsi || "-"}
                      </span>
                      <span className="text-muted">{rec.mic || "-"}</span>
                    </dd>
                  </>}
                </dl>
                {(rec.errors?.id_materiale_error === 1 ||
                  rec.errors?.id_provenienza_error === 1 ||
                  rec.errors?.id_micro_error === 1 ||
                  rec.errors?.id_antibiotico_error === 1) && (
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {rec.errors?.id_materiale_error === 1 && renderErrorChip("materialError", "id_materiale_error")}
                    {rec.errors?.id_provenienza_error === 1 && renderErrorChip("provenanceError", "id_provenienza_error")}
                    {rec.errors?.id_micro_error === 1 && renderErrorChip("microError", "id_micro_error")}
                    {rec.errors?.id_antibiotico_error === 1 && renderErrorChip("antibioticError", "id_antibiotico_error")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render LIS Header section
   */
  const renderLisHeader = (header) => {
    if (!header) {
      return (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          {t("noLisDataAvailable", { ns: "microDataLabels" })}
        </div>
      );
    }

    const isCollapsed = collapsedSections.lisHeader;

    return (
      <div className="card mb-3">
        <div
          className="card-header bg-primary text-white d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => toggleSection("lisHeader")}
        >
          <h5 className="mb-0 flex-grow-1">
            <i className="fas fa-file-medical me-2"></i>
            {t("lisHeaderTitle", { ns: "microDataLabels" })}
          </h5>
          <i
            className={`fas fa-chevron-${isCollapsed ? "down" : "up"} ms-2`}
            style={{ fontSize: "1.2rem" }}
          ></i>
        </div>
        {!isCollapsed && (
          <div className="card-body py-2 px-3">
            <dl className="row mb-0 small" style={{ rowGap: "0.15rem" }}>
              <dt className="col-5 text-muted fw-normal mb-0">{t("lisId", { ns: "microDataLabels" })}</dt>
              <dd className="col-7 mb-0 fw-semibold">{header.Lis_Id}</dd>

              <dt className="col-5 text-muted fw-normal mb-0">{t("patientName", { ns: "microDataLabels" })}</dt>
              <dd className="col-7 mb-0">
                {[header.Patient_Name, header.Patient_LastName].filter(Boolean).join(" ") || "-"}
              </dd>

              {header.Patient_CF && (
                <>
                  <dt className="col-5 text-muted fw-normal mb-0">{t("patientCF", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0 font-monospace">{header.Patient_CF}</dd>
                </>
              )}

              <dt className="col-5 text-muted fw-normal mb-0">{t("patientSex", { ns: "microDataLabels" })}</dt>
              <dd className="col-7 mb-0">{header.Patient_Sex || "-"}</dd>

              <dt className="col-5 text-muted fw-normal mb-0">{t("admittanceDate", { ns: "microDataLabels" })}</dt>
              <dd className="col-7 mb-0">{formatDate(header.Admittance_Date)}</dd>

              {header.ID_Ricovero && (
                <>
                  <dt className="col-5 text-muted fw-normal mb-0">{t("idRicovero", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0">{header.ID_Ricovero}</dd>
                </>
              )}

              {header.Data_Ricovero && (
                <>
                  <dt className="col-5 text-muted fw-normal mb-0">{t("dataRicovero", { ns: "microDataLabels" })}</dt>
                  <dd className="col-7 mb-0">{formatDate(header.Data_Ricovero)}</dd>
                </>
              )}

              <dt className="col-5 text-muted fw-normal mb-0">{t("businessUnitName", { ns: "microDataLabels" })}</dt>
              <dd className="col-7 mb-0">{header.business_unit_name || "-"}</dd>
            </dl>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render Colture section (Materials → Microorganisms → Antibiogramma + Geni di Resistenza)
   */
  const renderLisCulture = (materials, items, antibiotics, resistanceGenes) => {
    if (!materials || materials.length === 0) return null;

    const isCollapsed = collapsedSections.lisMaterials;

    return (
      <div className="card mb-3">
        <div
          className="card-header bg-success text-white d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => toggleSection("lisMaterials")}
        >
          <h5 className="mb-0 flex-grow-1">
            <i className="fas fa-flask me-2"></i>
            {t("lisMaterialsTitle", { ns: "microDataLabels" })}
            <span className="badge bg-light text-dark ms-2" style={{ fontSize: "0.75rem" }}>
              {materials.length}
            </span>
          </h5>
          <i className={`fas fa-chevron-${isCollapsed ? "down" : "up"} ms-2`} style={{ fontSize: "1.2rem" }}></i>
        </div>
        {!isCollapsed && (
          <div className="card-body py-2 px-3">
            {materials.map((mat, matIdx) => {
              const matItems = (items || []).filter((i) => i.Material_Id === mat.material_id);

              const cultureKey = mat.material_id || matIdx;
              const isCultureCollapsed = !!collapsedCultures[cultureKey];

              return (
                <div key={matIdx} className={matIdx < materials.length - 1 ? "mb-3" : ""}>
                  {/* Culture header — clickable toggle */}
                  <div
                    className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                    style={{ background: "#f0fdf4", borderLeft: "3px solid #22c55e", cursor: "pointer" }}
                    onClick={() => toggleCulture(cultureKey)}
                  >
                    <span className="badge bg-success" style={{ fontSize: "0.7rem" }}>
                      Coltura {matIdx + 1}
                    </span>
                    <span className="fw-semibold small text-success flex-grow-1">
                      {mat.material_description || mat.Material_Code || "-"}
                    </span>
                    {matItems.length > 0 && (
                      <span className="badge fw-normal me-1" style={{ background: "#dcfce7", color: "#16a34a", border: "1px solid #86efac", fontSize: "0.65rem" }}>
                        {matItems.length} {t("lisItemsTitle", { ns: "microDataLabels" })}
                      </span>
                    )}
                    <i className={`fas fa-chevron-${isCultureCollapsed ? "down" : "up"} text-success`} style={{ fontSize: "0.75rem" }}></i>
                  </div>

                  {!isCultureCollapsed && (
                    <>
                      {/* Material info */}
                      <dl className="row mb-0 small ps-2 mt-2" style={{ rowGap: "0.15rem" }}>
                        <dt className="col-5 text-muted fw-normal mb-0">{t("materialCodeStd", { ns: "microDataLabels" })}</dt>
                        <dd className="col-7 mb-0 fw-semibold">{mat.Material_Code || "-"}</dd>

                        <dt className="col-5 text-muted fw-normal mb-0">{t("materialDescription", { ns: "microDataLabels" })}</dt>
                        <dd className="col-7 mb-0">{mat.material_description || mat.Material_Source_Text || "-"}</dd>

                        <dt className="col-5 text-muted fw-normal mb-0">{t("provenanceCodeStd", { ns: "microDataLabels" })}</dt>
                        <dd className="col-7 mb-0">{mat.Provenance_Code || "-"}</dd>

                        <dt className="col-5 text-muted fw-normal mb-0">{t("provenanceDescription", { ns: "microDataLabels" })}</dt>
                        <dd className="col-7 mb-0">{mat.provenance_description || mat.Provenance_Source_Text || "-"}</dd>
                      </dl>

                      {/* Microorganisms for this culture */}
                      {matItems.length > 0 && (
                        <div className="mt-3 ps-2">
                          <div
                            className="d-flex align-items-center gap-2 mb-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleMicroSection(cultureKey)}
                          >
                            <h6 className="mb-0 small fw-semibold" style={{ color: "#92600a" }}>
                              <i className="fas fa-bacteria me-1"></i>
                              {t("lisItemsTitle", { ns: "microDataLabels" })}
                            </h6>
                            <span className="badge fw-normal" style={{ background: "#fef3c7", color: "#92600a", border: "1px solid #fcd34d", fontSize: "0.65rem" }}>
                              {matItems.length}
                            </span>
                            <i className={`fas fa-chevron-${collapsedMicroSections[cultureKey] ? "down" : "up"} ms-auto`} style={{ fontSize: "0.75rem", color: "#92600a" }}></i>
                          </div>
                          {!collapsedMicroSections[cultureKey] && matItems.map((item, itemIdx) => {
                            const microKey = item.item_id || `${cultureKey}_${itemIdx}`;
                            const isMicroCollapsed = !!collapsedMicroorganisms[microKey];
                            const relatedAntibiotics = (antibiotics || []).filter((ab) => ab.Lis_Item_Id === item.item_id);
                            const relatedGenes = (resistanceGenes || []).filter((g) => g.Lis_Item_Id === item.item_id);

                            return (
                              <div key={itemIdx} className={itemIdx < matItems.length - 1 ? "mb-2" : ""}>
                                {/* Microorganism header — clickable toggle */}
                                <div
                                  className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                                  style={{ background: "#fffbeb", borderLeft: "3px solid #f59e0b", cursor: "pointer" }}
                                  onClick={() => toggleMicroorganism(microKey)}
                                >
                                  <i className="fas fa-bacteria text-warning" style={{ fontSize: "0.75rem" }}></i>
                                  <span className="fw-semibold small flex-grow-1" style={{ color: "#92600a" }}>
                                    {item.microorganism_description || item.moCode || "-"}
                                  </span>
                                  <span className="text-muted small me-1">{item.moCode}</span>
                                  {item.is_sentinel_pathogen === 1 && (
                                    <span className="badge badge-danger" style={{ fontSize: "0.65rem" }} title={t("sentinelTooltip", { ns: "microDataLabels" })}>
                                      <i className="fas fa-exclamation-circle me-1"></i>
                                      {t("isSentinelPathogen", { ns: "microDataLabels" })}
                                    </span>
                                  )}
                                  {relatedAntibiotics.length > 0 && (
                                    <span className="badge fw-normal" style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontSize: "0.65rem" }}>
                                      <i className="fas fa-pills me-1"></i>{relatedAntibiotics.length}
                                    </span>
                                  )}
                                  {relatedGenes.length > 0 && (
                                    <span className="badge fw-normal" style={{ background: "#f3e8ff", color: "#7c3aed", border: "1px solid #c4b5fd", fontSize: "0.65rem" }}>
                                      <i className="fas fa-dna me-1"></i>{relatedGenes.length}
                                    </span>
                                  )}
                                  <i className={`fas fa-chevron-${isMicroCollapsed ? "down" : "up"}`} style={{ fontSize: "0.75rem", color: "#92600a" }}></i>
                                </div>

                                {!isMicroCollapsed && (
                                  <div className="ps-3 border-start border-warning pt-2">
                                    {/* Antibiogramma */}
                                    {relatedAntibiotics.length > 0 && (
                                      <div className="mb-2">
                                        <h6 className="text-primary small mb-1">
                                          <i className="fas fa-pills me-1"></i>
                                          {t("lisAntibioticsTitle", { ns: "microDataLabels" })}
                                        </h6>
                                        <div className="table-responsive" style={{ maxHeight: 280, overflowY: "auto" }}>
                                          <table className="table table-sm table-bordered mb-0">
                                            <thead className="table-light">
                                              <tr>
                                                <th style={{ whiteSpace: "nowrap" }}>{t("atcCode", { ns: "microDataLabels" })}</th>
                                                <th>{t("antibioticDescription", { ns: "microDataLabels" })}</th>
                                                <th style={{ whiteSpace: "nowrap" }}>{t("mic", { ns: "microDataLabels" })}</th>
                                                <th style={{ whiteSpace: "nowrap" }}>{t("rsi", { ns: "microDataLabels" })}</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {relatedAntibiotics.map((ab, abIdx) => (
                                                <tr key={abIdx}>
                                                  <td style={{ whiteSpace: "nowrap" }}>{ab.code || ab.ATC_Code || "-"}</td>
                                                  <td>{ab.antibiotic_description || "-"}</td>
                                                  <td style={{ whiteSpace: "nowrap" }}>{ab.MIC || "-"}</td>
                                                  <td style={{ whiteSpace: "nowrap" }}>
                                                    <span className={`badge ${ab.RSI === "R" ? "badge-danger" : ab.RSI === "S" ? "badge-success" : "badge-warning"}`}>
                                                      {ab.RSI || "-"}
                                                    </span>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}

                                    {/* Geni di Resistenza */}
                                    {relatedGenes.length > 0 && (
                                      <div>
                                        <h6 className="small mb-1" style={{ color: "#7c3aed" }}>
                                          <i className="fas fa-dna me-1"></i>
                                          Geni di Resistenza
                                        </h6>
                                        <div className="table-responsive">
                                          <table className="table table-sm table-bordered mb-0">
                                            <thead className="table-light">
                                              <tr>
                                                <th style={{ whiteSpace: "nowrap" }}>Codice Gene</th>
                                                <th>Descrizione</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {relatedGenes.map((g, gIdx) => (
                                                <tr key={gIdx}>
                                                  <td>
                                                    <span className="badge fw-normal" style={{ background: "#f3e8ff", color: "#7c3aed", border: "1px solid #c4b5fd" }}>
                                                      {g.gene_code || "-"}
                                                    </span>
                                                  </td>
                                                  <td>{g.voc_gene_description || <span className="text-muted">—</span>}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const selectedRecord = selectedRequestId
    ? data.find((r) => r.id_richiesta === selectedRequestId)
    : null;
  const selectedDetails = selectedRequestId
    ? lisDetails[selectedRequestId]
    : null;
  const isSelectedLoading = selectedRequestId
    ? loadingDetails.has(selectedRequestId)
    : false;
  const selectedIndex = selectedRequestId
    ? data.findIndex((r) => r.id_richiesta === selectedRequestId)
    : -1;
  const prevRequestId =
    selectedIndex > 0 ? data[selectedIndex - 1]?.id_richiesta : null;
  const nextRequestId =
    selectedIndex >= 0 && selectedIndex < data.length - 1
      ? data[selectedIndex + 1]?.id_richiesta
      : null;

  return (
    <>
      {/* Drawer-specific CSS overrides.
          Some themes apply sticky/positioned card headers globally, which can cause
          sections to overlap inside a scroll container. We hard-disable that inside
          the microbiological drawer. */}
      <style>{`
        /* Strong overrides: some themes make card headers sticky inside scroll containers. */
        .micro-data-drawer .card-header,
        .micro-data-drawer .card .card-header,
        .micro-data-drawer .card > .card-header,
        .micro-data-drawer .sticky-top {
          position: relative !important; /* keep in normal flow, but override sticky/absolute */
          top: auto !important;
          z-index: auto !important;
          transform: none !important;
          margin-top: 0 !important;
        }
        .micro-data-drawer .card {
          position: relative !important;
          margin-bottom: 1rem;
          height: auto !important;
          min-height: 0 !important;
          overflow: visible !important; /* do NOT clip inner content */
        }
        .micro-data-drawer .card-body {
          position: relative !important;
          height: auto !important;       /* prevent huge white areas */
          min-height: 0 !important;      /* prevent huge white areas */
        }
        /* Improve readability in narrow drawer without breaking labels like '...:'.
           Some theme CSS uses very aggressive wrapping (e.g. overflow-wrap:anywhere).
           We override it here with !important so labels don't split and leave ':' alone. */
        .micro-data-drawer, 
        .micro-data-drawer * {
          word-break: normal !important;
          overflow-wrap: break-word !important;
          hyphens: none !important;
        }

        /* Labels: allow wrapping at spaces (avoid overflow), but never break inside words */
        .micro-data-drawer strong,
        .micro-data-drawer b,
        .micro-data-drawer .card-header * {
          display: inline-block !important;
          max-width: 100% !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
          hyphens: none !important;
        }

        /* Hover highlight for main results table */
        .micro-data-table tbody tr {
          cursor: pointer;
          transition: background-color 120ms ease-in-out;
        }
        .micro-data-table tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.12) !important;
        }
        /* When using striped tables, cells often have background set; override per-cell too */
        .micro-data-table tbody tr:hover > td,
        .micro-data-table tbody tr:hover > th {
          background-color: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.12) !important;
        }
      `}</style>

      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>
      <div className="container-fluid">
        <div className="card-body p-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto">
              <div className="row">
                <div className="col-12">
                  <div className="col-12">
                    <h3 className="mb-0">
                      <i className="fas fa-solid fa-microscope me-2"></i>
                      {t("screenTitle", { ns: "microDataLabels" })}
                    </h3>
                    <p className="text-muted">
                      {t("screenDescription", {
                        ns: "microDataLabels",
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <>
                <LisTableHeader
                  filtersConfig={[
                    {
                      key: "requestId",
                      label: t("requestId", { ns: "microDataLabels" }),
                      type: "text",
                      placeholder: t("requestId", { ns: "microDataLabels" }),
                    },
                    {
                      key: "fromDate",
                      label: t("admittanceFromDate", { ns: "microDataLabels" }),
                      type: "date",
                    },
                    {
                      key: "toDate",
                      label: t("admittanceToDate", { ns: "microDataLabels" }),
                      type: "date",
                    },
                    {
                      key: "gender",
                      label: t("gender", { ns: "microDataLabels" }),
                      type: "select",
                      options: [
                        {
                          value: "M",
                          label: t("male", { ns: "microDataLabels" }),
                        },
                        {
                          value: "F",
                          label: t("female", { ns: "microDataLabels" }),
                        },
                      ],
                      placeholder: t("gender", { ns: "microDataLabels" }),
                    },
                    {
                      key: "businessUnit",
                      label: t("businessUnit", { ns: "microDataLabels" }),
                      type: "text",
                      placeholder: t("businessUnit", { ns: "microDataLabels" }),
                    },
                    {
                      key: "testDescription",
                      label: t("testDescription", { ns: "microDataLabels" }),
                      type: "text",
                      placeholder: t("testDescription", {
                        ns: "microDataLabels",
                      }),
                    },
                    {
                      key: "errorFilter",
                      label: t("error", { ns: "lisLabels" }),
                      type: "select",
                      options: [
                        { label: t("hasErrors", { ns: "microDataLabels" }), value: "true" },
                        { label: t("noErrors", { ns: "microDataLabels" }), value: "false" },
                      ],
                      placeholder: t("error", { ns: "lisLabels" }),
                    },
                  ]}
                  values={filters}
                  onChange={(key, value) =>
                    setFilters({ ...filters, [key]: value })
                  }
                  onSearch={() => {
                    handleSearch();
                    setShowReset(true);
                  }}
                  onReset={clearFilters}
                  isLoading={isLoading}
                  uniformControlStyle={uniformControlStyle}
                  columnClass="col-auto"
                  buttonLabels={{
                    search: t("searchButton", { ns: "microDataLabels" }),
                    reset: t("clearFiltersButton", { ns: "microDataLabels" }),
                  }}
                />
                <div className="card">
                  <div className="card-body">
                    <LisMainTable
                      headers={[
                        { key: "icon", label: t("open", { ns: "microDataLabels" }) },
                        {
                          key: "id_richiesta",
                          label: t("requestId", { ns: "microDataLabels" }),
                        },
                        {
                          key: "data_accettazione",
                          label: t("admittanceDate", { ns: "microDataLabels" }),
                        },
                        {
                          key: "patient",
                          label: t("patient", { ns: "microDataLabels" }),
                        },
                        {
                          key: "sesso",
                          label: t("sex", { ns: "microDataLabels" }),
                        },
                        {
                          key: "descrizione_reparto",
                          label: t("department", { ns: "microDataLabels" }),
                        },
                        {
                          key: "descrizione_analisi",
                          label: t("testDescription", {
                            ns: "microDataLabels",
                          }),
                        },
                        {
                          key: "__errors__",
                          label: t("error", { ns: "lisLabels" }),
                        },
                      ]}
                      rows={data}
                      isLoading={isLoading}
                      onRowClick={(row) =>
                        toggleDrawerForRequest(row.id_richiesta)
                      }
                      getRowKey={(row) => row.id_richiesta}
                      getRowTitle={() =>
                        t("expandTooltip", { ns: "microDataLabels" })
                      }
                      getRowStyle={() => ({ cursor: "pointer" })}
                      renderCell={({ row, header }) => {
                        if (header.key === "icon") {
                          return (
                            <i className="fas fa-solid fa-folder-tree"></i>
                          );
                        }
                        if (header.key === "id_richiesta") {
                          return row.id_richiesta;
                        }
                        if (header.key === "data_accettazione") {
                          return formatDate(row.data_accettazione);
                        }
                        if (header.key === "patient") {
                          return `${row.Patient_Name || ""} ${row.Patient_LastName || ""}`.trim() || "-";
                        }
                        if (header.key === "sesso") {
                          return row.sesso || "-";
                        }
                        if (header.key === "descrizione_reparto") {
                          return (
                            <div
                              style={{
                                wordWrap: "break-word",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                maxWidth: "200px",
                                minWidth: "120px",
                              }}
                            >
                              {row.descrizione_reparto || "-"}
                            </div>
                          );
                        }
                        if (header.key === "descrizione_analisi") {
                          return (
                            <div
                              style={{
                                wordWrap: "break-word",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                maxWidth: "200px",
                                minWidth: "120px",
                              }}
                            >
                              {row.descrizione_analisi || "-"}
                            </div>
                          );
                        }
                        if (header.key === "__errors__") {
                          const recordHasErrors = hasErrors(row);
                          const errorReasonsTitle = recordHasErrors
                            ? getErrorLabelKeys(row.errors)
                              .map((k) => t(k, { ns: "microDataLabels" }))
                              .join(", ")
                            : "";
                          return (
                            <span
                              title={
                                recordHasErrors
                                  ? errorReasonsTitle
                                  : t("noErrors", { ns: "microDataLabels" })
                              }
                              style={{ cursor: "help" }}
                            >
                              <span >{recordHasErrors ? "🔴" : "🟢"}</span>
                            </span>
                          );
                        }
                        return null;
                      }}
                      pagination={pagination || undefined}
                      onPageChange={(page) => handlePageChange(page)}
                      maxHeight={450}
                    />
                  </div>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>

      {/* Right-side drawer for details */}
      {isDrawerOpen && selectedRequestId && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => {
              setIsDrawerOpen(false);
              setSelectedRequestId(null);
            }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 1050,
            }}
          />

          {/* Drawer */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              height: "100vh",
              width: `${DRAWER_WIDTH}px`,
              maxWidth: "95vw",
              background: "#fff",
              zIndex: 1051,
              boxShadow: "-8px 0 24px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
            }}
            className="micro-data-drawer"
          >
            <div
              className="border-bottom px-3 py-2 d-flex align-items-center justify-content-between"
              style={{ background: "var(--bs-body-bg)" }}
            >
              <div className="d-flex flex-column">
                <strong>
                  {t("requestId", { ns: "microDataLabels" })}:{" "}
                  {selectedRequestId}
                  {selectedRecord && (
                    <span className="ms-2 text-muted fw-normal">
                      ({selectedRecord.Patient_Name || ""} {selectedRecord.Patient_LastName || ""})
                    </span>
                  )}
                </strong>
                <small className="text-muted">
                  {t("screenTitle", { ns: "microDataLabels" })}
                </small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted d-none d-sm-inline">
                  {selectedIndex >= 0
                    ? `${selectedIndex + 1}/${data.length}`
                    : ""}
                </small>

                {/* Expand/Collapse All button */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const allCollapsed = Object.values(collapsedSections).every(
                      (v) => v === true
                    );
                    setCollapsedSections({
                      errorLegend: !allCollapsed,
                      sourceData: !allCollapsed,
                      lisHeader: !allCollapsed,
                      lisMaterials: !allCollapsed,
                    });
                  }}
                  title={
                    Object.values(collapsedSections).every((v) => v === true)
                      ? "Expand All"
                      : "Collapse All"
                  }
                >
                  <i
                    className={`fas ${Object.values(collapsedSections).every((v) => v === true)
                      ? "fa-solid fa-angles-down"
                      : "fa-solid fa-angles-down fa-rotate-180"
                      }`}
                  ></i>
                </button>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={!prevRequestId || isSelectedLoading}
                  onClick={() =>
                    prevRequestId && openDrawerForRequest(prevRequestId)
                  }
                  aria-label={t("previousRecord", { ns: "microDataLabels" })}
                  title={t("previousRecord", { ns: "microDataLabels" })}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={!nextRequestId || isSelectedLoading}
                  onClick={() =>
                    nextRequestId && openDrawerForRequest(nextRequestId)
                  }
                  aria-label={t("nextRecord", { ns: "microDataLabels" })}
                  title={t("nextRecord", { ns: "microDataLabels" })}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setIsDrawerOpen(false);
                    setSelectedRequestId(null);
                  }}
                  aria-label={t("closeDrawer", { ns: "microDataLabels" })}
                  title={t("closeDrawer", { ns: "microDataLabels" })}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div
              className="micro-data-drawer-content"
              style={{
                overflowY: "auto",
                padding: "1rem",
                position: "relative",
              }}
            >
              {isSelectedLoading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      {t("loadingDetails", { ns: "microDataLabels" })}
                    </span>
                  </div>
                  <p className="mt-2 mb-0">
                    {t("loadingDetails", { ns: "microDataLabels" })}
                  </p>
                </div>
              ) : (
                <>
                  {/* Error legend (if record has errors) */}
                  {selectedRecord &&
                    hasErrors(selectedRecord) &&
                    renderErrorLegend()}

                  {/* Source data (NOMOS) */}
                  {selectedRecord ? renderSourceData(selectedRecord) : null}

                  {/* LIS details */}
                  {selectedDetails ? (
                    <>
                      {renderLisHeader(selectedDetails.header)}
                      {renderLisCulture(
                        selectedDetails.materials,
                        selectedDetails.items,
                        selectedDetails.antibiotics || [],
                        selectedDetails.resistance_genes || []
                      )}
                    </>
                  ) : (
                    <div className="alert alert-info mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      {t("noLisDataAvailable", { ns: "microDataLabels" })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MicrobiologicalDataExplorerScreen;
